/** Progressive enhancement: native Shopify form + native cart remain the fallback. */
if (!customElements.get('av-product')) {
  customElements.define('av-product', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.listenOptions = { signal: this.abortController.signal };
      this.initializeGallery();
      this.initializeSizeGuide();
      const variantData = this.querySelector('[data-product-variants]');
      if (!variantData) return;
      try { this.variants = JSON.parse(variantData.textContent); } catch { return; }
      this.form = this.querySelector('.av-product__form');
      this.variantSelect = this.querySelector('[data-variant-select]');
      this.quantity = this.querySelector('[data-quantity]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      if (!this.form || !this.variantSelect || !this.quantity || !this.addButton) return;
      this.querySelector('[data-options]')?.removeAttribute('hidden');
      this.classList.add('is-ready');
      this.querySelectorAll('[data-quantity-step]').forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => {
          if (Number(button.dataset.quantityStep) > 0) this.quantity.stepUp();
          else this.quantity.stepDown();
          this.syncQuantityButtons();
        }, this.listenOptions);
      });
      this.quantity.addEventListener('input', () => this.syncQuantityButtons(), this.listenOptions);
      this.querySelector('[data-options]')?.addEventListener('change', () => this.selectVariant(), this.listenOptions);
      this.form.addEventListener('submit', (event) => this.addToCart(event), this.listenOptions);
      const initialVariantId = this.variantSelect.dataset.initialVariant || this.variantSelect.value;
      this.updateVariant(this.variants.find((variant) => variant.id === initialVariantId), false);
      this.initializeWishlist();
    }
    disconnectedCallback() {
      this.abortController?.abort();
      this.galleryObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }
    initializeGallery() {
      this.gallery = this.querySelector('[data-gallery]');
      this.slides = [...this.querySelectorAll('[data-media-id]')];
      this.thumbnails = [...this.querySelectorAll('[data-thumbnail]')];
      this.galleryIndex = 0;
      if (!this.gallery || !this.slides.length) return;
      this.thumbnails.forEach((thumbnail, index) => thumbnail.addEventListener('click', (event) => {
        event.preventDefault();
        this.showImage(index);
      }, this.listenOptions));
      this.querySelectorAll('[data-gallery-step]').forEach((button) => {
        button.hidden = false;
        button.addEventListener('click', () => this.showImage(this.galleryIndex + Number(button.dataset.galleryStep)), this.listenOptions);
      });
      this.gallery.addEventListener('keydown', (event) => {
        if (event.target !== this.gallery) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.showImage(this.galleryIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, this.listenOptions);
      if ('IntersectionObserver' in window) {
        this.galleryObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio > .6) this.setGalleryIndex(this.slides.indexOf(entry.target));
          });
        }, { root: this.gallery, threshold: .65 });
        this.slides.forEach((slide) => this.galleryObserver.observe(slide));
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.gallery.scrollTo({ left: this.gallery.clientWidth * this.galleryIndex, behavior: 'instant' }));
        this.resizeObserver.observe(this.gallery);
      }
    }
    showImage(index) {
      const next = (index + this.slides.length) % this.slides.length;
      this.gallery.scrollTo({ left: this.gallery.clientWidth * next, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      this.setGalleryIndex(next);
    }
    setGalleryIndex(index) {
      this.galleryIndex = index;
      this.thumbnails.forEach((thumbnail, current) => {
        if (index === current) thumbnail.setAttribute('aria-current', 'true');
        else thumbnail.removeAttribute('aria-current');
      });
      const count = this.querySelector('[data-gallery-count]');
      if (count) count.textContent = `${index + 1} / ${this.slides.length}`;
      this.slides.forEach((slide, current) => {
        if (current !== index) slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    }
    selectVariant() {
      const choices = [...this.querySelectorAll('[data-option-index]')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
      const variant = this.variants.find((item) => item.options.every((option, index) => option === choices[index]));
      this.updateVariant(variant, true);
    }
    updateVariant(variant, updateUrl) {
      this.currentVariant = variant;
      this.variantSelect.value = variant?.id || '';
      this.variantSelect.disabled = !variant;
      const price = this.querySelector('[data-price]');
      if (price && variant) {
        price.replaceChildren();
        const amount = document.createElement('span');
        amount.textContent = variant.price;
        price.append(amount);
        if (variant.comparePrice) {
          const comparison = document.createElement('s');
          comparison.textContent = variant.comparePrice;
          price.append(comparison);
        }
      } else if (price) price.textContent = 'Unavailable combination';
      this.addButton.disabled = !variant?.available;
      this.querySelector('[data-add-label]').textContent = !variant ? 'Unavailable' : variant.available ? 'Add to cart' : 'Sold out';
      this.querySelector('[data-product-error]').hidden = true;
      this.querySelector('[data-cart-confirmation]').hidden = true;
      if (variant) {
        this.quantity.min = variant.minimum;
        this.quantity.step = variant.increment;
        if (variant.maximum != null) this.quantity.max = variant.maximum;
        else this.quantity.removeAttribute('max');
        const previous = Number(this.quantity.value);
        const normalized = Math.max(variant.minimum, variant.minimum + Math.floor((previous - variant.minimum) / variant.increment) * variant.increment);
        this.quantity.value = variant.maximum == null ? normalized : Math.min(variant.maximum, normalized);
        const rules = [];
        if (variant.minimum > 1) rules.push(`Minimum ${variant.minimum}.`);
        if (variant.increment > 1) rules.push(`Increments of ${variant.increment}.`);
        if (variant.maximum != null) rules.push(`Maximum ${variant.maximum}.`);
        const note = this.querySelector('[data-quantity-note]');
        note.textContent = rules.join(' ');
        note.hidden = !rules.length;
        const imageIndex = this.slides.findIndex((slide) => slide.dataset.mediaId === variant.mediaId);
        if (imageIndex !== -1) this.showImage(imageIndex);
        if (updateUrl && window.location.pathname === this.dataset.productUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url);
        }
      }
      this.syncQuantityButtons();
      this.syncAvailability();
    }
    syncAvailability() {
      const fields = [...this.querySelectorAll('[data-option-index]')];
      const choices = fields.map((field) => field.querySelector('input:checked')?.value);
      fields.forEach((field, index) => {
        const currentValue = field.querySelector('[data-option-current]');
        if (currentValue) currentValue.textContent = choices[index] || '';
        field.querySelectorAll('input').forEach((input) => {
          const available = this.variants.some((variant) => variant.available && variant.options.every((value, position) => value === (position === index ? input.value : choices[position])));
          input.closest('label').classList.toggle('is-unavailable', !available);
          // Keep unavailable options selectable so shoppers can inspect sold-out variants.
          input.setAttribute('aria-label', `${input.value}${available ? '' : ' — unavailable with the other selected options'}`);
        });
      });
    }
    syncQuantityButtons() {
      const value = Number(this.quantity.value);
      const minimum = Number(this.quantity.min) || 1;
      const increment = Number(this.quantity.step) || 1;
      const maximum = this.quantity.max ? Number(this.quantity.max) : Infinity;
      this.querySelector('[data-quantity-step="-1"]').disabled = value <= minimum;
      this.querySelector('[data-quantity-step="1"]').disabled = value + increment > maximum;
    }
    async addToCart(event) {
      if (!window.fetch || !this.currentVariant) return;
      event.preventDefault();
      if (this.submitting || !this.currentVariant.available || !this.form.reportValidity()) return;
      this.submitting = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      this.querySelector('[data-add-label]').textContent = 'Adding…';
      const error = this.querySelector('[data-product-error]');
      error.hidden = true;
      const body = new FormData(this.form);
      [...body.keys()].filter((name) => name.startsWith('av-option-')).forEach((name) => body.delete(name));
      try {
        const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body, credentials: 'same-origin' });
        const result = await response.json();
        if (!response.ok || result.status) {
          const description = typeof result.description === 'string' ? result.description : 'This option could not be added. Please check your selection and try again.';
          throw Object.assign(new Error(description), { confirmedError: true });
        }
        this.querySelector('[data-add-label]').textContent = 'Added to your bag';
        // Full navigation keeps Horizon cart, count, localization and checkout in sync.
        window.location.assign(this.dataset.cartUrl);
      } catch (failure) {
        error.textContent = failure.confirmedError ? failure.message : 'We could not confirm the update. Please check your bag before trying again.';
        error.hidden = false;
        this.querySelector('[data-cart-confirmation]').hidden = false;
        this.addButton.disabled = !this.currentVariant?.available;
        this.querySelector('[data-add-label]').textContent = this.currentVariant?.available ? 'Add to cart' : 'Sold out';
        this.submitting = false;
      } finally {
        this.addButton.removeAttribute('aria-busy');
      }
    }
    initializeSizeGuide() {
      const dialog = this.querySelector('[data-size-guide]');
      const opener = this.querySelector('[data-size-guide-open]');
      if (!dialog || !opener || typeof dialog.showModal !== 'function') return;
      opener.addEventListener('click', (event) => { event.preventDefault(); dialog.showModal(); }, this.listenOptions);
      dialog.querySelector('[data-size-guide-close]').addEventListener('click', () => dialog.close(), this.listenOptions);
      dialog.addEventListener('click', (event) => {
        if (event.target !== dialog) return;
        const bounds = dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) dialog.close();
      }, this.listenOptions);
      dialog.addEventListener('close', () => opener.focus(), this.listenOptions);
    }
    initializeWishlist() {
      const button = this.querySelector('[data-wishlist]');
      if (!button || !this.dataset.productId) return;
      const key = 'auve-valen-wishlist';
      const readSaved = () => {
        try { const value = JSON.parse(localStorage.getItem(key) || '[]'); return Array.isArray(value) ? value.filter((id) => typeof id === 'string') : []; }
        catch { return []; }
      };
      const update = () => {
        const saved = readSaved().includes(this.dataset.productId);
        button.setAttribute('aria-pressed', String(saved));
        button.querySelector('[data-wishlist-label]').textContent = saved ? 'Saved to wishlist' : 'Save to wishlist';
      };
      button.hidden = false;
      update();
      button.addEventListener('click', () => {
        const saved = readSaved();
        const exists = saved.includes(this.dataset.productId);
        const next = exists ? saved.filter((id) => id !== this.dataset.productId) : [...saved, this.dataset.productId];
        const status = this.querySelector('[data-wishlist-status]');
        try { localStorage.setItem(key, JSON.stringify(next)); update(); status.textContent = exists ? 'Removed from your wishlist on this device.' : 'Saved to your wishlist on this device.'; }
        catch { status.textContent = 'Your browser could not save this item. Please bookmark this page instead.'; }
      }, this.listenOptions);
      window.addEventListener('storage', (event) => { if (event.key === key) update(); }, this.listenOptions);
    }
  });
}
